import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { TopBar } from "../components/Navigation";
import { useAuth } from "../lib/AuthContext";
import { Trophy, Users, Calendar, ArrowLeft, Crown, Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";

const ACTION_LABELS = {
    adventure_posted: "adventure posts",
    gps_comment: "trail notes",
    save_received: "saves received",
    review_written: "reviews written",
};

export default function FriendChallengeDetail() {
    const { cid } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await api.get(`/friend-challenges/${cid}`);
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e));
            navigate("/friend-challenges");
        } finally { setLoading(false); }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [cid]);

    if (loading) return <div className="p-6 text-sm text-slate-gray">Loading…</div>;
    if (!data) return null;

    const isCreator = user?.user_id === data.creator_id;
    const isParticipant = (data.participants || []).includes(user?.user_id);

    const join = async () => {
        setBusy(true);
        try {
            await api.post(`/friend-challenges/${cid}/join`);
            toast.success("Joined the challenge");
            await refresh();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    const leave = async () => {
        if (!window.confirm("Leave this challenge?")) return;
        setBusy(true);
        try {
            await api.post(`/friend-challenges/${cid}/leave`);
            toast.success("Left challenge");
            navigate("/friend-challenges");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm("Delete this challenge for everyone?")) return;
        setBusy(true);
        try {
            await api.delete(`/friend-challenges/${cid}`);
            toast.success("Challenge deleted");
            navigate("/friend-challenges");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const ends = data.ends_at ? new Date(data.ends_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

    return (
        <div data-testid="fc-detail-page">
            <TopBar title={data.title} />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10 space-y-6">
                <Link to="/friend-challenges" className="inline-flex items-center gap-1 text-sm text-slate-gray hover:text-ember-orange">
                    <ArrowLeft className="w-4 h-4" /> All friend challenges
                </Link>

                <header className="bg-snowcap border border-ridge-line rounded-xl p-6">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2 ${
                                data.status === "active" ? "bg-moss/15 text-moss" : "bg-trail-tan text-granite"
                            }`}>{data.status}</span>
                            <h1 className="font-display font-black text-3xl text-midnight-pine tracking-tight">{data.title}</h1>
                            {data.description && <p className="text-sm text-granite mt-2">{data.description}</p>}
                        </div>
                    </div>
                    <dl className="grid grid-cols-3 gap-3 mt-4 text-sm">
                        <Stat label="Goal" value={`${data.target_count} ${ACTION_LABELS[data.action_kind] || data.action_kind}`} />
                        <Stat label="Participants" value={`${data.participants?.length || 0}`} icon={Users} />
                        <Stat label="Ends" value={ends || "—"} icon={Calendar} />
                    </dl>
                    <div className="flex flex-wrap gap-2 mt-4">
                        {!isParticipant && data.status === "active" && (
                            <button onClick={join} disabled={busy} data-testid="fc-join-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark active:scale-95 transition-all">
                                Join challenge
                            </button>
                        )}
                        {isParticipant && !isCreator && (
                            <button onClick={leave} disabled={busy} data-testid="fc-leave-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-ridge-line text-granite text-sm font-bold hover:border-midnight-pine">
                                <LogOut className="w-4 h-4" /> Leave
                            </button>
                        )}
                        {isCreator && (
                            <button onClick={remove} disabled={busy} data-testid="fc-delete-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-red-300 text-red-600 text-sm font-bold hover:bg-red-50">
                                <Trash2 className="w-4 h-4" /> Delete
                            </button>
                        )}
                    </div>
                </header>

                <section data-testid="fc-leaderboard" className="bg-snowcap border border-ridge-line rounded-xl p-6">
                    <header className="flex items-center gap-2 mb-4">
                        <Trophy className="w-4 h-4 text-ember-orange" />
                        <h2 className="font-display font-bold text-lg text-midnight-pine">Leaderboard</h2>
                    </header>
                    {data.leaderboard?.length === 0 ? (
                        <p className="text-sm text-slate-gray">No one has joined yet.</p>
                    ) : (
                        <ol className="space-y-2">
                            {data.leaderboard.map((row, i) => {
                                const pct = Math.min(100, Math.round((row.score / data.target_count) * 100));
                                return (
                                    <li key={row.user_id} data-testid={`fc-row-${row.user_id}`} className={`flex items-center gap-3 p-3 rounded-lg ${
                                        row.completed ? "bg-moss/10 border border-moss/30" : "border border-ridge-line"
                                    }`}>
                                        <span className="font-display font-black text-2xl text-midnight-pine w-8 text-center">{i + 1}</span>
                                        <div className="w-9 h-9 rounded-full bg-trail-tan overflow-hidden flex items-center justify-center text-sm font-bold text-midnight-pine shrink-0">
                                            {row.user.avatar
                                                ? <img src={row.user.avatar} alt={row.user.name} className="w-full h-full object-cover" />
                                                : (row.user.name || "?").charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1">
                                                <Link to={`/profile/${row.user_id}`} className="font-bold text-sm text-midnight-pine truncate hover:text-ember-orange">
                                                    {row.user.name}
                                                </Link>
                                                {row.user_id === data.creator_id && <Crown className="w-3 h-3 text-ember-orange" />}
                                                {row.completed && <span className="text-[10px] font-bold uppercase tracking-widest text-moss ml-1">DONE</span>}
                                            </div>
                                            <div className="h-1.5 bg-trail-tan/60 rounded-full overflow-hidden mt-1">
                                                <div className={`h-full ${row.completed ? "bg-moss" : "bg-ember-orange"}`} style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-display font-black text-xl text-midnight-pine">{row.score}</div>
                                            <div className="text-[10px] uppercase tracking-widest text-slate-gray">/ {data.target_count}</div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </section>
            </div>
        </div>
    );
}

function Stat({ label, value, icon: Icon }) {
    return (
        <div className="bg-trail-tan/30 rounded-md p-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray flex items-center gap-1 mb-1">
                {Icon && <Icon className="w-3 h-3" />}{label}
            </div>
            <div className="font-bold text-sm text-midnight-pine truncate">{value}</div>
        </div>
    );
}
